import React, { useState, useEffect } from 'react';
import { getSession } from '../../../utils/auth';

const ProfileSettings = () => {
  const session = getSession();

  const [form, setForm] = useState({
    name: '',
    phone: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'Norge',
    language: 'no',
  });

  const [pwForm, setPwForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [pwErrors, setPwErrors] = useState({});
  const [pwVisible, setPwVisible] = useState({ current: false, new: false, confirm: false });

  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (session) {
      setForm({
        name: session?.name || session?.fullName || '',
        phone: session?.phone || '',
        street: session?.address?.street || '',
        postalCode: session?.address?.postalCode || '',
        city: session?.address?.city || '',
        country: session?.address?.country || 'Norge',
        language: session?.language || 'no',
      });
    }
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const isNorwegian = form?.language === 'no';

  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: 'transparent', width: '0%' };
    let score = 0;
    if (password?.length >= 8) score++;
    if (password?.length >= 12) score++;
    if (/[A-Z]/?.test(password)) score++;
    if (/[0-9]/?.test(password)) score++;
    if (/[^A-Za-z0-9]/?.test(password)) score++;
    if (score <= 1) return { score, label: isNorwegian ? 'Svak' : 'Weak', color: '#EF4444', width: '25%' };
    if (score <= 3) return { score, label: isNorwegian ? 'Middels' : 'Medium', color: '#F59E0B', width: '60%' };
    return { score, label: isNorwegian ? 'Sterk' : 'Strong', color: '#22C55E', width: '100%' };
  };

  const strength = getPasswordStrength(pwForm?.newPassword);

  const handlePwChange = (field, value) => {
    setPwForm(prev => ({ ...prev, [field]: value }));
    setPwErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePasswordUpdate = () => {
    const errors = {};
    const storedSession = getSession();
    const storedPassword = storedSession?.password || '';

    if (!pwForm?.currentPassword) {
      errors.currentPassword = isNorwegian ? 'Nåværende passord er påkrevd' : 'Current password is required';
    } else if (pwForm?.currentPassword !== storedPassword) {
      errors.currentPassword = isNorwegian ? 'Feil passord' : 'Incorrect password';
    }
    if (!pwForm?.newPassword) {
      errors.newPassword = isNorwegian ? 'Nytt passord er påkrevd' : 'New password is required';
    } else if (pwForm?.newPassword?.length < 8) {
      errors.newPassword = isNorwegian ? 'Passordet må ha minst 8 tegn' : 'Password must be at least 8 characters';
    }
    if (!pwForm?.confirmPassword) {
      errors.confirmPassword = isNorwegian ? 'Bekreft passordet' : 'Please confirm your password';
    } else if (pwForm?.newPassword !== pwForm?.confirmPassword) {
      errors.confirmPassword = isNorwegian ? 'Passordene stemmer ikke overens' : 'Passwords do not match';
    }

    if (Object.keys(errors)?.length > 0) {
      setPwErrors(errors);
      return;
    }

    try {
      const existing = getSession();
      const updated = { ...existing, password: pwForm?.newPassword };
      localStorage.setItem('veiassistent_session', JSON.stringify(updated));
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPwErrors({});
      showToast(isNorwegian ? 'Passord oppdatert' : 'Password updated', 'success');
    } catch {
      showToast(isNorwegian ? 'Kunne ikke oppdatere passord' : 'Could not update password', 'error');
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = () => {
    try {
      const existing = getSession();
      const updated = {
        ...existing,
        name: form?.name,
        phone: form?.phone,
        language: form?.language,
        address: {
          street: form?.street,
          postalCode: form?.postalCode,
          city: form?.city,
          country: form?.country,
        },
      };
      localStorage.setItem('veiassistent_session', JSON.stringify(updated));
      showToast(form?.language === 'no' ? 'Profil lagret' : 'Profile saved', 'success');
    } catch {
      showToast('Kunne ikke lagre profil', 'error');
    }
  };

  const inputStyle = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid var(--color-border)',
    borderRadius: 0,
    padding: '10px 0',
    fontFamily: 'var(--font-body)',
    fontSize: '15px',
    color: 'var(--color-foreground)',
    outline: 'none',
    transition: 'border-color 200ms ease',
  };

  const labelStyle = {
    display: 'block',
    fontFamily: 'var(--font-heading)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--color-muted-foreground)',
    marginBottom: '6px',
  };

  const fieldGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div className="card" style={{ position: 'relative' }}>
      {/* Toast notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: '32px',
            right: '32px',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 22px',
            background: toast?.type === 'success' ? 'var(--color-foreground)' : '#DC2626',
            borderRadius: 'var(--radius-sm)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            animation: 'slideInUp 0.3s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <style>{`
            @keyframes slideInUp {
              from { opacity: 0; transform: translateY(16px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          {toast?.type === 'success' && (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" stroke="#4ADE80" strokeWidth="1.5"/>
              <path d="M5.5 9L7.8 11.5L12.5 6.5" stroke="#4ADE80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', letterSpacing: '0.04em' }}>
            {toast?.message}
          </span>
        </div>
      )}
      <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '32px' }}>
        {isNorwegian ? 'Profilinnstillinger' : 'Profile Settings'}
      </h3>
      {/* Personal Info */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
          <div style={{ width: '3px', height: '18px', background: 'var(--color-accent)', borderRadius: '2px' }} />
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)' }}>
            {isNorwegian ? 'Personlig informasjon' : 'Personal Information'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '28px' }}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{isNorwegian ? 'Fullt navn' : 'Full Name'}</label>
            <input
              type="text"
              value={form?.name}
              onChange={e => handleChange('name', e?.target?.value)}
              placeholder={isNorwegian ? 'Ola Nordmann' : 'John Doe'}
              style={inputStyle}
              onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
            />
          </div>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{isNorwegian ? 'Telefonnummer' : 'Phone Number'}</label>
            <input
              type="tel"
              value={form?.phone}
              onChange={e => handleChange('phone', e?.target?.value)}
              placeholder="+47 000 00 000"
              style={inputStyle}
              onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
            />
          </div>
        </div>
      </div>
      {/* Address */}
      <div style={{ marginBottom: '40px', paddingTop: '32px', borderTop: '2px solid var(--color-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
          <div style={{ width: '3px', height: '18px', background: 'var(--color-accent)', borderRadius: '2px' }} />
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)' }}>
            {isNorwegian ? 'Adresse' : 'Address'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '28px' }}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{isNorwegian ? 'Gateadresse' : 'Street Address'}</label>
            <input
              type="text"
              value={form?.street}
              onChange={e => handleChange('street', e?.target?.value)}
              placeholder={isNorwegian ? 'Storgata 1' : '123 Main Street'}
              style={inputStyle}
              onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '28px' }}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>{isNorwegian ? 'Postnummer' : 'Postal Code'}</label>
              <input
                type="text"
                value={form?.postalCode}
                onChange={e => handleChange('postalCode', e?.target?.value)}
                placeholder="0150"
                style={inputStyle}
                onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
                onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
              />
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>{isNorwegian ? 'By' : 'City'}</label>
              <input
                type="text"
                value={form?.city}
                onChange={e => handleChange('city', e?.target?.value)}
                placeholder="Oslo"
                style={inputStyle}
                onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
                onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
              />
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>{isNorwegian ? 'Land' : 'Country'}</label>
              <input
                type="text"
                value={form?.country}
                onChange={e => handleChange('country', e?.target?.value)}
                placeholder="Norge"
                style={inputStyle}
                onFocus={e => e.target.style.borderBottomColor = 'var(--color-accent)'}
                onBlur={e => e.target.style.borderBottomColor = 'var(--color-border)'}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Language Preference */}
      <div style={{ marginBottom: '40px', paddingTop: '32px', borderTop: '2px solid var(--color-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
          <div style={{ width: '3px', height: '18px', background: 'var(--color-accent)', borderRadius: '2px' }} />
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)' }}>
            {isNorwegian ? 'Språkpreferanse' : 'Language Preference'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0', background: 'rgba(0,0,0,0.04)', borderRadius: 'var(--radius-sm)', padding: '4px', width: 'fit-content', border: '2px solid var(--color-border)' }}>
          {[
            { value: 'no', label: 'Norsk', flag: '🇳🇴' },
            { value: 'en', label: 'English', flag: '🇬🇧' },
          ]?.map(option => (
            <button
              key={option?.value}
              onClick={() => handleChange('language', option?.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 20px',
                borderRadius: 'calc(var(--radius-sm) - 2px)',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-heading)',
                fontSize: '13px',
                fontWeight: form?.language === option?.value ? 700 : 500,
                letterSpacing: '0.04em',
                transition: 'all 200ms cubic-bezier(0.22,1,0.36,1)',
                background: form?.language === option?.value ? 'var(--color-accent)' : 'transparent',
                color: form?.language === option?.value ? '#FFFFFF' : 'var(--color-muted-foreground)',
                boxShadow: form?.language === option?.value ? '0 2px 8px rgba(255,87,34,0.3)' : 'none',
              }}
            >
              <span style={{ fontSize: '16px' }}>{option?.flag}</span>
              {option?.label}
            </button>
          ))}
        </div>
        <p style={{ marginTop: '12px', fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)' }}>
          {isNorwegian
            ? 'Velger visningsspråk for grensesnittet'
            : 'Sets the display language for the interface'}
        </p>
      </div>
      {/* Password Management */}
      <div style={{ marginBottom: '40px', paddingTop: '32px', borderTop: '2px solid var(--color-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
          <div style={{ width: '3px', height: '18px', background: 'var(--color-accent)', borderRadius: '2px' }} />
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)' }}>
            {isNorwegian ? 'Endre passord' : 'Change Password'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '28px' }}>
          {/* Current Password */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{isNorwegian ? 'Nåværende passord' : 'Current Password'}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={pwVisible?.current ? 'text' : 'password'}
                value={pwForm?.currentPassword}
                onChange={e => handlePwChange('currentPassword', e?.target?.value)}
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: '36px', borderBottomColor: pwErrors?.currentPassword ? '#EF4444' : 'var(--color-border)' }}
                onFocus={e => e.target.style.borderBottomColor = pwErrors?.currentPassword ? '#EF4444' : 'var(--color-accent)'}
                onBlur={e => e.target.style.borderBottomColor = pwErrors?.currentPassword ? '#EF4444' : 'var(--color-border)'}
              />
              <button
                type="button"
                onClick={() => setPwVisible(prev => ({ ...prev, current: !prev?.current }))}
                style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-muted-foreground)' }}
              >
                {pwVisible?.current
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
            {pwErrors?.currentPassword && (
              <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: '#EF4444', marginTop: '6px' }}>{pwErrors?.currentPassword}</span>
            )}
          </div>
          {/* New Password */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{isNorwegian ? 'Nytt passord' : 'New Password'}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={pwVisible?.new ? 'text' : 'password'}
                value={pwForm?.newPassword}
                onChange={e => handlePwChange('newPassword', e?.target?.value)}
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: '36px', borderBottomColor: pwErrors?.newPassword ? '#EF4444' : 'var(--color-border)' }}
                onFocus={e => e.target.style.borderBottomColor = pwErrors?.newPassword ? '#EF4444' : 'var(--color-accent)'}
                onBlur={e => e.target.style.borderBottomColor = pwErrors?.newPassword ? '#EF4444' : 'var(--color-border)'}
              />
              <button
                type="button"
                onClick={() => setPwVisible(prev => ({ ...prev, new: !prev?.new }))}
                style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-muted-foreground)' }}
              >
                {pwVisible?.new
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
            {pwErrors?.newPassword && (
              <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: '#EF4444', marginTop: '6px' }}>{pwErrors?.newPassword}</span>
            )}
            {/* Strength indicator */}
            {pwForm?.newPassword && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', color: 'var(--color-muted-foreground)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {isNorwegian ? 'Styrke' : 'Strength'}
                  </span>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 700, color: strength?.color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {strength?.label}
                  </span>
                </div>
                <div style={{ height: '4px', background: 'var(--color-border)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: strength?.width, background: strength?.color, borderRadius: '2px', transition: 'width 300ms cubic-bezier(0.22,1,0.36,1), background 300ms ease' }} />
                </div>
              </div>
            )}
          </div>
          {/* Confirm Password */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>{isNorwegian ? 'Bekreft nytt passord' : 'Confirm New Password'}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={pwVisible?.confirm ? 'text' : 'password'}
                value={pwForm?.confirmPassword}
                onChange={e => handlePwChange('confirmPassword', e?.target?.value)}
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: '36px', borderBottomColor: pwErrors?.confirmPassword ? '#EF4444' : 'var(--color-border)' }}
                onFocus={e => e.target.style.borderBottomColor = pwErrors?.confirmPassword ? '#EF4444' : 'var(--color-accent)'}
                onBlur={e => e.target.style.borderBottomColor = pwErrors?.confirmPassword ? '#EF4444' : 'var(--color-border)'}
              />
              <button
                type="button"
                onClick={() => setPwVisible(prev => ({ ...prev, confirm: !prev?.confirm }))}
                style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-muted-foreground)' }}
              >
                {pwVisible?.confirm
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
            {pwErrors?.confirmPassword && (
              <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: '#EF4444', marginTop: '6px' }}>{pwErrors?.confirmPassword}</span>
            )}
            {pwForm?.confirmPassword && !pwErrors?.confirmPassword && pwForm?.newPassword === pwForm?.confirmPassword && (
              <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: '#22C55E', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#22C55E" strokeWidth="1.2"/><path d="M3.5 6L5.2 7.8L8.5 4.5" stroke="#22C55E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                {isNorwegian ? 'Passordene stemmer overens' : 'Passwords match'}
              </span>
            )}
          </div>
        </div>
        {/* Update Password Button */}
        <div style={{ marginTop: '28px' }}>
          <button
            onClick={handlePasswordUpdate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '11px 28px',
              background: 'transparent',
              border: '2px solid var(--color-accent)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-heading)',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-accent)',
              cursor: 'pointer',
              transition: 'all 200ms cubic-bezier(0.22,1,0.36,1)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.color = '#FFFFFF'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(255,87,34,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--color-accent)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
            {isNorwegian ? 'Oppdater passord' : 'Update Password'}
          </button>
        </div>
      </div>
      {/* Save Button */}
      <div style={{ paddingTop: '32px', borderTop: '2px solid var(--color-border)' }}>
        <button
          onClick={handleSave}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '13px 32px',
            background: 'var(--color-accent)',
            border: '2px solid var(--color-accent)',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-heading)',
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#FFFFFF',
            cursor: 'pointer',
            transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,87,34,0.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          onMouseDown={e => e.currentTarget.style.transform = 'scale(0.96)'}
          onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {isNorwegian ? 'Lagre profil' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
};

export default ProfileSettings;
